import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import ValidationContext from 'ia-react-core/~/components/form/_ValidationContext';
import { MessageMap } from 'ia-react-core/~/interfaces/MessageMap';
import { Validator } from 'ia-react-core/~/interfaces/Validator';
import { ValidatorConfig } from 'ia-react-core/~/interfaces/ValidatorConfig';
import { ValidatorMap } from 'ia-react-core/~/interfaces/ValidatorMap';

import { ControlInfo } from 'ia-react-core/~/interfaces/ControlInfo';
import { MessageTypeFn } from 'ia-react-core/~/interfaces/MessageType';
import ControlMessageContext from 'ia-react-core/~/components/form/_ControlMessageContext';
import { FormErrors } from 'ia-react-core/~/interfaces/FormState';
import useTranslation from '../useTranslation';

interface UseValidationMessagesOptions<Value> {
  value?: Value;
  messages?: MessageMap<Value>;
  errors?: Record<string, boolean | string> | FormErrors;
  validators?: ValidatorMap<Value>;
  controls?: ControlInfo[];
}

const useValidationMessages = <Value, >({ value, messages, errors, controls = [], validators = {} }: UseValidationMessagesOptions<Value>) => {
  const { t } = useTranslation();
  const defaultMessages = useMemo(() => ({ messages: {} }), []);
  const [globalMessages, setGlobalMessages] = useState<MessageMap<Value>>({});
  const { messages: validationContextMessages } = (useContext(ValidationContext) || defaultMessages) as { messages: MessageMap<Value>};

  const onGlobalMessagesChange = useCallback((newCurrentMessages: MessageMap<Value>) => {
    setGlobalMessages(newCurrentMessages);
  }, []);

  useEffect(() => {
    ValidationContext.addGlobalMessagesListener<Value>(onGlobalMessagesChange);
    return () => {
      ValidationContext.removeGlobalMessagesListener?.(onGlobalMessagesChange);
    };
  }, [onGlobalMessagesChange]);

  const resolvedMessages: MessageMap<Value> = useMemo(() => ({
    ...globalMessages,
    ...validationContextMessages,
    ...messages,
  }), [globalMessages, messages, validationContextMessages]);

  const errorTypes = useMemo(() => (
    Object.keys(errors).filter((errorType) => errors[errorType])
  ), [errors]);

  const getValidationMessage = useCallback((validatorType: string, errorType: string, isCoreMessage: boolean) => {
    if (isCoreMessage) {
      const extension = (errors[validatorType] as string).split(':')?.[1];
      const extensionValidationMessage = resolvedMessages[`${validatorType}:${extension}`];

      if (extensionValidationMessage) {
        return { validationMessage: extensionValidationMessage, isExtended: true };
      }
    }

    return { validationMessage: resolvedMessages[errorType] || resolvedMessages[validatorType] || resolvedMessages.default };
  }, [errors, resolvedMessages]);

  const wrapMessage = useCallback((message: ReactNode, { name, formScope }: { name?: string; formScope?: string[] }) => (
    typeof message === 'string' ? message : <ControlMessageContext.Provider value={{ name, formScope }}>{message}</ControlMessageContext.Provider>
  ), []);

  const errorMessages = useMemo(() => {
    const controlEls = Array.from(window.document.querySelectorAll('[data-control'));
    const controlIndexMap = controlEls.reduce<Record<string, number>>((result, controlsEl, index) => {
      result[controlsEl.id] = index;
      return result;
    }, {});

    return errorTypes
      .sort((errorTypeA, errorTypeB) => {
        const errorTypePartsA = errorTypeA.split('__').slice(0, -1);
        const errorTypePartsB = errorTypeB.split('__').slice(0, -1);
        const controlA = controls.find((info) => [...info.formScope.slice(1), info.name].join('__') === errorTypePartsA.join('__'));
        const controlB = controls.find((info) => [...info.formScope.slice(1), info.name].join('__') === errorTypePartsB.join('__'));
        const indexA = controlIndexMap?.[controlA?.id];
        const indexB = controlIndexMap?.[controlB?.id];
        if (indexA < indexB) return -1;
        if (indexA > indexB) return 1;
        return 0;
      })
      .map((errorType) => {
        const errorTypeParts = errorType.split('__');
        const validatorType = errorTypeParts.pop();
        const isStringError = (typeof errors[errorType] === 'string');
        const isCoreMessage = Boolean(isStringError && (errors[errorType] as string).startsWith('Core/'));
        const { validationMessage, isExtended } = getValidationMessage(validatorType, errorType, isCoreMessage);
        const validator = (typeof validators[errorType] === 'object') ? (validators[errorType] as ValidatorConfig<Value>).validator : validators[errorType] as Validator<Value>;
        const validatorConfig: ValidatorConfig<Value> = (typeof validators[errorType] === 'object') ? validators[errorType] as ValidatorConfig<Value> : {} as ValidatorConfig<Value>;
        const validatorOptions = validatorConfig && 'options' in validatorConfig && validatorConfig?.options;

        const controlInfo = controls.find((info) => [...info.formScope.slice(1), info.name].join('__') === errorTypeParts.join('__'));

        if (typeof errors[errorType] === 'string' && !isExtended) {
          return {
            type: errorType,
            message: t(String(errors[errorType]), validatorOptions),
            exclusive: validatorConfig.exclusive,
          };
        }

        if (validatorConfig.message && typeof validatorConfig.message !== 'function') {
          return {
            type: errorType,
            message: wrapMessage(validatorConfig.message, { name: controlInfo?.name, formScope: controlInfo?.formScope }),
            exclusive: validatorConfig.exclusive,
          };
        }

        if (typeof validationMessage === 'function') {
          const resolvedMessage = (validatorConfig.message as MessageTypeFn<Value>) || validationMessage;
          return {
            type: errorType,
            message: wrapMessage(resolvedMessage(
              value,
              { ...validatorOptions, ...(validator?.computed?.(value, validatorOptions)) },
            ), { name: controlInfo?.name, formScope: controlInfo?.formScope }),
            exclusive: validatorConfig.exclusive,
          };
        }

        return {
          type: errorType,
          message: wrapMessage(validationMessage || '', { name: controlInfo?.name, formScope: controlInfo?.formScope }),
          exclusive: validatorConfig.exclusive,
        };
      });
  }, [errorTypes, errors, getValidationMessage, validators, controls, wrapMessage, t, value]);

  const filteredErrorMessages = useMemo(() => {
    const exclusiveMessage = errorMessages.find(({ exclusive }) => exclusive);

    if (exclusiveMessage) {
      return [exclusiveMessage];
    }

    return errorMessages;
  }, [errorMessages]);

  return filteredErrorMessages;
};

export default useValidationMessages;
